import { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';

// The shared "benefits projected to begin" line. The engine computes a single
// `monthsFromIssueToFirstPayout` and uses it verbatim as the premium period, so on
// a standard (non-limited-pay) policy the premium schedule sums to exactly this
// figure — the premium table and the monthly breakdown can't drift. The copy
// states only the benefit-timeline fact (when the first payout lands), NOT a claim
// about premiums: limited-pay policies stop premiums BEFORE benefits begin, so the
// premium total can be smaller.
//
// Two presentations share one sentence: a visible block (`BenefitsBeginAnchor`,
// used in the Policy Cost section) and a hover trigger (`BenefitsBeginTooltip`,
// used in the Monthly Breakdown, where it doesn't need to be always visible).

function formatYearsMonths(months: number): string {
  const years = Math.floor(months / 12);
  const remainder = months % 12;
  if (years > 0 && remainder > 0) return `${years} yr ${remainder} mo`;
  if (years > 0) return `${years} yr`;
  return `${remainder} mo`;
}

// `emphasisClass` colors the bold spans so the same copy reads correctly on a
// light block (dark text) and inside a dark tooltip surface (light text). Returns
// null when there is no future first-payout anchor — the projection never pays, or
// pays immediately at issue (no future point to count down to).
function benefitsBeginSentence(terms: ClaimTerms, emphasisClass: string): ReactNode | null {
  const months = terms.monthsFromIssueToFirstPayout;
  const policyYear = terms.firstPayoutPolicyYear;
  if (months == null || months <= 0 || policyYear == null) return null;
  const { firstPayoutCalendarYear } = terms;

  return (
    <>
      Benefits projected to begin in{' '}
      <span className={`font-medium ${emphasisClass}`}>
        policy year {policyYear}
        {firstPayoutCalendarYear != null ? ` · ${firstPayoutCalendarYear}` : ''}
      </span>{' '}
      —{' '}
      <span className={`font-medium tabular-nums ${emphasisClass}`}>
        {months} months ({formatYearsMonths(months)})
      </span>{' '}
      after issue.
    </>
  );
}

// Always-visible block — used in the Policy Cost & Premiums section.
export function BenefitsBeginAnchor({ terms }: { terms: ClaimTerms }) {
  const sentence = benefitsBeginSentence(terms, 'text-gray-900');
  if (!sentence) return null;
  return (
    <p className="mb-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">{sentence}</p>
  );
}

// Hover trigger — used in the Monthly Breakdown, where the timeline doesn't need
// to occupy space at the top. Light emphasis for the dark tooltip surface.
export function BenefitsBeginTooltip({ terms }: { terms: ClaimTerms }) {
  const sentence = benefitsBeginSentence(terms, 'text-gray-100');
  if (!sentence) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help whitespace-nowrap border-b border-dotted border-gray-400 text-xs text-gray-600">
          When benefits begin
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm text-xs leading-snug text-gray-200">
        {sentence}
      </TooltipContent>
    </Tooltip>
  );
}
